import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  LoadingSkeleton,
  Modal,
  Pagination,
  Select,
  useToast,
} from '@nexora/design-system';
import {
  useShopFloorCycles,
  useStartCycle,
  useCompleteCycle,
  type ShopFloorCycle,
} from './useShopFloor';
import { useProductionOrders } from './useProductionOrders';
import { formatDateTime } from '../../shared/utils/format';

/* ── Contrato (backend: shop-floor.controller.ts + manufacturing-extended.dto.ts) ──
 *
 * GET  /manufacturing/shop-floor?all=&cellName=&machineName=&productionOrderId=&page=&limit=
 *      -> { data: ShopFloorCycleResponseDto[], total, page, limit } (default: apenas ativos)
 * POST /manufacturing/shop-floor           body: { productionOrderId?, cellName, machineName?, notes? }
 * POST /manufacturing/shop-floor/:id/complete body: { producedQuantity, qualityStatus, notes? }
 *
 * ShopFloorCycleResponseDto: id, productionOrderId?, cellName, machineName?, status,
 *   startedAt, endedAt?, durationMinutes, producedQuantity, qualityStatus, notes?, createdAt
 *
 * Tela prototipo "Apontamento de Producao (Shop Floor)": ID Ciclo, Hora Fim, Tempo Ciclo,
 * Qtd Produzida, Status Qualidade + iniciar/finalizar ciclo.
 */

const QUALITY_VARIANT: Record<string, 'success' | 'error' | 'warning' | 'neutral'> = {
  APPROVED: 'success',
  REJECTED: 'error',
  REWORK: 'warning',
  PENDING: 'neutral',
};

const QUALITY_LABEL: Record<string, string> = {
  APPROVED: 'OK',
  REJECTED: 'Refugo',
  REWORK: 'Retrabalho',
  PENDING: 'Pendente',
};

/** Duracao em minutos -> HH:MM:SS (mesmo formato do prototipo 00:01:45). */
function formatDuration(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 0) return '—';
  const total = Math.round(minutes * 60);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
}

export function ShopFloorPage(): JSX.Element {
  const { addToast } = useToast();

  const [showAll, setShowAll] = useState<'ACTIVE' | 'ALL'>('ACTIVE');
  const [cellFilter, setCellFilter] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading, error, refetch } = useShopFloorCycles({
    all: showAll === 'ALL',
    cellName: cellFilter || undefined,
    page,
    limit,
  });
  const cycles = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const { data: ordersData } = useProductionOrders({ page: 1, limit: 100 });
  const orders = ordersData?.data ?? [];
  const activeRatio = useMemo(() => {
    if (!data || data.total === 0) return 0;
    const active = data.data.filter(
      (c) => c.status === 'RUNNING' || c.status === 'PAUSED',
    ).length;
    return Math.round((active / data.data.length) * 100);
  }, [data]);

  const [startOpen, setStartOpen] = useState(false);
  const [completeTarget, setCompleteTarget] = useState<ShopFloorCycle | null>(null);

  const [startForm, setStartForm] = useState({
    productionOrderId: '',
    cellName: '',
    machineName: '',
    notes: '',
  });
  const [completeForm, setCompleteForm] = useState({
    producedQuantity: '',
    qualityStatus: 'APPROVED' as 'PENDING' | 'APPROVED' | 'REJECTED' | 'REWORK',
    notes: '',
  });

  const startMutation = useStartCycle();
  const completeMutation = useCompleteCycle();

  const handleStart = async (): Promise<void> => {
    if (!startForm.cellName.trim()) {
      addToast('Informe a célula de produção', 'error');
      return;
    }
    try {
      await startMutation.mutateAsync({
        cellName: startForm.cellName.trim(),
        machineName: startForm.machineName.trim() || undefined,
        productionOrderId: startForm.productionOrderId || undefined,
        notes: startForm.notes.trim() || undefined,
      });
      addToast('Ciclo iniciado', 'success');
      setStartOpen(false);
      setStartForm({ productionOrderId: '', cellName: '', machineName: '', notes: '' });
      setPage(1);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Erro ao iniciar ciclo', 'error');
    }
  };

  const handleComplete = async (): Promise<void> => {
    if (!completeTarget) return;
    const qty = Number(completeForm.producedQuantity);
    if (!Number.isFinite(qty) || qty < 0) {
      addToast('Informe a quantidade produzida', 'error');
      return;
    }
    try {
      await completeMutation.mutateAsync({
        id: completeTarget.id,
        producedQuantity: qty,
        qualityStatus: completeForm.qualityStatus,
        notes: completeForm.notes.trim() || undefined,
      });
      addToast('Ciclo finalizado', 'success');
      setCompleteTarget(null);
      setCompleteForm({ producedQuantity: '', qualityStatus: 'APPROVED', notes: '' });
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Erro ao finalizar ciclo', 'error');
    }
  };

  return (
    <div className="shop-floor-page">
      <style>{`
        .shop-floor-page { padding: 1.5rem; }
        .shop-floor-table-wrap { overflow-x: auto; }
        .shop-floor-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .shop-floor-table th { text-align: left; padding: 0.5rem 0.75rem; font-weight: 600; color: #43474e; border-bottom: 1px solid #c4c6cf; background: #eceef0; white-space: nowrap; }
        .shop-floor-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #e0e3e5; }
        .shop-floor-num { text-align: right; font-variant-numeric: tabular-nums; font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .shop-floor-empty { padding: 2rem 1rem; text-align: center; color: #43474e; }
        .shop-floor-error { padding: 1.5rem; text-align: center; color: #ba1a1a; }
        .shop-floor-filters { display: flex; gap: 1rem; flex-wrap: wrap; align-items: flex-end; margin-bottom: 1rem; }
        .shop-floor-pagination { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 1rem; gap: 0.5rem; flex-wrap: wrap; }
      `}</style>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <p className="font-label-sm text-label-sm text-on-surface-variant mb-xs uppercase tracking-wider">
            Produção / Chão de Fábrica
          </p>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Apontamento de Produção (Shop Floor)
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Ciclos de célula/máquina: inicie uma operação, apure a quantidade e a qualidade ao fechar.
          </p>
        </div>
        <Button onClick={() => { setStartOpen(true); setPage(1); }}>+ Iniciar Operação</Button>
      </div>

      {/* Indicador rápido: ciclos ativos na página atual */}
      {!isLoading && !error && cycles.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-md mb-lg">
          <Card padding="md">
            <div className="font-label-md text-label-md text-on-surface-variant uppercase">Ciclos listados</div>
            <div className="font-display-lg text-display-lg text-on-surface tabular-nums">{cycles.length}</div>
          </Card>
          <Card padding="md">
            <div className="font-label-md text-label-md text-on-surface-variant uppercase">Ativos (RUNNING/PAUSED)</div>
            <div className="font-display-lg text-display-lg text-on-surface tabular-nums">
              {cycles.filter((c) => c.status === 'RUNNING' || c.status === 'PAUSED').length}
            </div>
          </Card>
          <Card padding="md">
            <div className="font-label-md text-label-md text-on-surface-variant uppercase">Proporção ativa</div>
            <div className="font-display-lg text-display-lg text-on-surface tabular-nums">{activeRatio}%</div>
          </Card>
        </div>
      )}

      <div className="shop-floor-filters">
        <Select
          label="Exibição"
          value={showAll}
          onChange={(e) => { setShowAll(e.target.value as 'ACTIVE' | 'ALL'); setPage(1); }}
        >
          <option value="ACTIVE">Somente ciclos ativos</option>
          <option value="ALL">Todos (incluir fechados)</option>
        </Select>
        <Input
          label="Célula"
          placeholder="Filtrar por célula..."
          value={cellFilter}
          onChange={(e) => { setCellFilter(e.target.value); setPage(1); }}
        />
      </div>

      {isLoading && (
        <Card padding="1rem">
          <LoadingSkeleton count={6} height="48px" />
        </Card>
      )}

      {error && !isLoading && (
        <Card padding="2rem">
          <div className="shop-floor-error" role="alert">
            <p>Erro ao carregar os ciclos do chão de fábrica</p>
            <p className="font-body-sm text-body-sm text-on-surface-variant">{(error as Error).message}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        </Card>
      )}

      {!isLoading && !error && cycles.length === 0 && (
        <Card padding="2rem">
          <div className="shop-floor-empty">
            {showAll === 'ACTIVE' && !cellFilter
              ? 'Nenhum ciclo ativo no momento. Clique em "+ Iniciar Operação" para abrir um ciclo.'
              : 'Nenhum ciclo encontrado com os filtros aplicados.'}
          </div>
        </Card>
      )}

      {!isLoading && !error && cycles.length > 0 && (
        <Card padding="0">
          <CardHeader
            className="px-md pt-md"
            action={
              <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                {total} {total === 1 ? 'ciclo' : 'ciclos'}
              </span>
            }
          >
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">
              Ciclos registrados
            </h3>
          </CardHeader>
          <div className="shop-floor-table-wrap">
            <table className="shop-floor-table">
              <thead>
                <tr>
                  <th>ID Ciclo</th>
                  <th>Célula / Máquina</th>
                  <th>Hora Fim</th>
                  <th style={{ textAlign: 'right' }}>Tempo Ciclo</th>
                  <th style={{ textAlign: 'right' }}>Qtd Produzida</th>
                  <th>Status Qualidade</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {cycles.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <span
                        className="font-label-md text-label-md text-primary"
                        style={{ fontFamily: 'monospace', fontWeight: 600 }}
                        title={c.id}
                      >
                        #{c.id.slice(0, 8)}
                      </span>
                    </td>
                    <td>
                      {c.cellName}
                      {c.machineName ? (
                        <span className="font-label-sm text-label-sm text-on-surface-variant"> · {c.machineName}</span>
                      ) : null}
                    </td>
                    <td>{c.endedAt ? formatDateTime(c.endedAt) : <Badge variant="info" size="sm">Ativo</Badge>}</td>
                    <td className="shop-floor-num">{formatDuration(c.durationMinutes)}</td>
                    <td className="shop-floor-num">{c.producedQuantity}</td>
                    <td>
                      <Badge variant={QUALITY_VARIANT[c.qualityStatus] ?? 'neutral'} size="sm">
                        {QUALITY_LABEL[c.qualityStatus] ?? c.qualityStatus}
                      </Badge>
                    </td>
                    <td>
                      {c.status === 'RUNNING' || c.status === 'PAUSED' ? (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => {
                            setCompleteTarget(c);
                            setCompleteForm({
                              producedQuantity: String(c.producedQuantity ?? ''),
                              qualityStatus: 'APPROVED',
                              notes: '',
                            });
                          }}
                        >
                          Finalizar
                        </Button>
                      ) : (
                        <span className="font-label-sm text-label-sm text-on-surface-variant">
                          {c.status === 'COMPLETED' ? 'Fechado' : 'Cancelado'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="shop-floor-pagination">
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              <span className="font-label-sm text-label-sm text-on-surface-variant">
                Página {page} de {totalPages}
              </span>
            </div>
          )}
        </Card>
      )}

      {/* Iniciar ciclo */}
      <Modal
        isOpen={startOpen}
        onClose={() => setStartOpen(false)}
        title="Iniciar Operação"
        footer={
          <>
            <Button variant="ghost" onClick={() => setStartOpen(false)}>Cancelar</Button>
            <Button onClick={handleStart} loading={startMutation.isPending}>Iniciar</Button>
          </>
        }
      >
        <div className="flex flex-col gap-md">
          <Input
            label="Célula de Produção *"
            placeholder="Ex: Célula de Usinagem 02"
            value={startForm.cellName}
            onChange={(e) => setStartForm((f) => ({ ...f, cellName: e.target.value }))}
          />
          <Input
            label="Máquina"
            placeholder="Ex: Torno CNC-3"
            value={startForm.machineName}
            onChange={(e) => setStartForm((f) => ({ ...f, machineName: e.target.value }))}
          />
          <Select
            label="OP vinculada (opcional)"
            value={startForm.productionOrderId}
            onChange={(e) => setStartForm((f) => ({ ...f, productionOrderId: e.target.value }))}
          >
            <option value="">Sem OP vinculada</option>
            {orders.map((o) => (
              <option key={o.id} value={o.id}>
                {o.orderNumber} — {o.productName}
              </option>
            ))}
          </Select>
          <Input
            label="Observações"
            placeholder="Observações de início"
            value={startForm.notes}
            onChange={(e) => setStartForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </div>
      </Modal>

      {/* Finalizar ciclo */}
      <Modal
        isOpen={!!completeTarget}
        onClose={() => setCompleteTarget(null)}
        title={completeTarget ? `Finalizar Ciclo #${completeTarget.id.slice(0, 8)}` : 'Finalizar Ciclo'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCompleteTarget(null)}>Cancelar</Button>
            <Button onClick={handleComplete} loading={completeMutation.isPending}>Finalizar</Button>
          </>
        }
      >
        <div className="flex flex-col gap-md">
          <Input
            label="Qtd Produzida *"
            type="number"
            min="0"
            placeholder="0"
            value={completeForm.producedQuantity}
            onChange={(e) => setCompleteForm((f) => ({ ...f, producedQuantity: e.target.value }))}
          />
          <Select
            label="Status Qualidade"
            value={completeForm.qualityStatus}
            onChange={(e) =>
              setCompleteForm((f) => ({
                ...f,
                qualityStatus: e.target.value as 'PENDING' | 'APPROVED' | 'REJECTED' | 'REWORK',
              }))
            }
          >
            <option value="APPROVED">OK — Aprovado</option>
            <option value="REJECTED">Refugo — Rejeitado</option>
            <option value="REWORK">Retrabalho</option>
            <option value="PENDING">Pendente de inspeção</option>
          </Select>
          <Input
            label="Observações"
            placeholder="Observações de fechamento"
            value={completeForm.notes}
            onChange={(e) => setCompleteForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </div>
      </Modal>
    </div>
  );
}